/** Flow 2 — Accueil dans l'application web (simulation, no backend) */

export const FLOW2_TITLE = "Flow 2 — Onboarding élève";

export const FLOW2_STEPS = [
  "Message de bienvenue",
  "Rôle du tuteur",
  "Élève prêt",
  "Disponibilités de base",
  "Nom du tuteur",
];

export const TUTOR_NAME_OPTIONS = ["Léo", "Zoé", "Nico", "Mila"];

const WEEKDAY_SLOTS = ["16 h – 18 h", "18 h – 20 h"];

export const BASE_AVAILABILITY = [
  { id: "lun", label: "Lundi", slots: WEEKDAY_SLOTS },
  { id: "mar", label: "Mardi", slots: WEEKDAY_SLOTS },
  { id: "mer", label: "Mercredi", slots: WEEKDAY_SLOTS },
  { id: "jeu", label: "Jeudi", slots: WEEKDAY_SLOTS },
  { id: "ven", label: "Vendredi", slots: WEEKDAY_SLOTS },
  { id: "sam", label: "Samedi", slots: ["9 h – 12 h", "13 h – 16 h", "16 h – 19 h"] },
  { id: "dim", label: "Dimanche", slots: ["9 h – 12 h", "13 h – 16 h", "16 h – 19 h"] },
];

export function slotId(dayId, slot) {
  return `f2-slot-${dayId}-${slot.replace(/[^0-9]+/g, "-").replace(/^-|-$/g, "")}`;
}

export function defaultAvailability() {
  return BASE_AVAILABILITY.flatMap((day) => day.slots.map((slot) => slotId(day.id, slot)));
}

export const FLOW2_REPLIES = {
  welcome: [
    { id: "go", label: "Oui, c'est parti !", primary: true },
    { id: "later", label: "Peut-être plus tard", primary: false },
  ],
  understood: [{ id: "understood", label: "Super, j'ai compris !", primary: true }],
  ready: [
    { id: "ready", label: "Oui, je suis prêt(e) !", primary: true },
    { id: "question", label: "J'ai encore une question", primary: false },
  ],
  avail: [{ id: "f2-avail-ok", label: "Continuer", primary: true }],
  name: [
    ...TUTOR_NAME_OPTIONS.map((label) => ({
      id: `f2-name-${label
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")}`,
      label,
      primary: false,
    })),
    { id: "f2-name-random", label: "Choisis pour moi", primary: true },
    { id: "f2-name-other", label: "Autre choix", primary: false },
  ],
  baptism: [{ id: "f2-name-go", label: "Aller à mes objectifs", primary: true }],
};

export function cleanTutorName(value) {
  const name = String(value || "").replace(/\s+/g, " ").trim();
  if (name.length < 2 || name.length > 20) return "";
  if (!/^[\p{L}][\p{L} '\u2019-]*[\p{L}]$/u.test(name)) return "";
  return name;
}

export function pickTutorName(options = TUTOR_NAME_OPTIONS) {
  return options[Math.floor(Math.random() * options.length)];
}

export function welcomeMessage(studentName) {
  return `👋 Salut ${studentName} !\n\nBienvenue sur Plan Étude. Je suis ton tuteur virtuel et je t’accompagne dans ton parcours scolaire.\n\nMon objectif : t’aider à mieux t’organiser, progresser à ton rythme et rester motivé. Prêt(e) à commencer ?`;
}

export const ROLE_MESSAGE =
  "Tu peux me parler à tout moment : pour poser une question, demander un conseil ou partager un blocage. Je suis là pour t’accompagner pas à pas ! 💙";

export const READY_MESSAGE =
  "Je suis encore en développement, donc je te demande d'être conciliant avec moi !\n\nMais je m'améliore avec chaque interaction.\n\nEs-tu prêt(e) à commencer ton accompagnement ? 🚀";

export const AVAIL_BASE_MESSAGE =
  "Pour tes périodes d'étude à la maison. Coche les heures où tu peux étudier.";

export const NAME_MESSAGE =
  "Une dernière chose. Je n'ai pas encore de nom. Choisis-le : il restera visible, seulement sur ton compte 😊.";

export const NAME_OTHER_MESSAGE = "Quel prénom veux-tu me donner ?";

export const NAME_INVALID_MESSAGE =
  "Un prénom, simplement. Deux lettres au minimum.";

export function baptismMessage(tutorName, studentName) {
  return `${tutorName}, c'est moi. Enchanté, ${studentName}. On commence quand tu veux.`;
}

/** Instant history up to a sommaire step (0–2), with the prompt for that step. */
export function buildFlow2Snapshot(stepIndex, studentName) {
  const step = Math.min(Math.max(stepIndex, 0), FLOW2_STEPS.length - 1);
  const messages = [
    { sender: "agent", messageKey: "f2.welcome", text: welcomeMessage(studentName) },
  ];
  if (step === 0) {
    return snap(messages, FLOW2_REPLIES.welcome, 0, false);
  }
  messages.push({ sender: "user", text: "Oui, c'est parti !" });
  messages.push({ sender: "agent", messageKey: "f2.role", text: ROLE_MESSAGE });
  if (step === 1) {
    return snap(messages, FLOW2_REPLIES.understood, 1, false);
  }
  messages.push({ sender: "user", text: "Super, j'ai compris !" });
  messages.push({ sender: "agent", messageKey: "f2.ready", text: READY_MESSAGE });
  if (step === 2) {
    return snap(messages, FLOW2_REPLIES.ready, 2, false);
  }
  messages.push({ sender: "user", text: "Oui, je suis prêt(e) !" });
  messages.push({ sender: "agent", messageKey: "f2.avail", text: AVAIL_BASE_MESSAGE });
  messages.push({ sender: "agent", kind: "availability" });
  if (step === 3) {
    return snap(messages, FLOW2_REPLIES.avail, 3, false);
  }
  messages.push({ sender: "user", text: "Disponibilités notées" });
  messages.push({ sender: "agent", messageKey: "f2.name", text: NAME_MESSAGE });
  return snap(messages, FLOW2_REPLIES.name, 4, false);
}

function snap(messages, quickReplies, flowStep, planReady, status = FLOW2_STEPS[flowStep]) {
  return { messages, quickReplies, flowStep, status, planReady };
}

export const FLOW2_BENEFITS = [
  { icon: "⚡", title: "Simple et immédiat" },
  { icon: "👤", title: "Un tuteur virtuel toujours disponible" },
  { icon: "📅", title: "Un suivi personnalisé chaque semaine" },
  { icon: "📄", title: "Des progrès concrets pas à pas" },
];
